<script setup lang="ts">
  import { ButtonVariant } from '@tundr/theme';
  import {
    Button,
    ButtonColor,
    ButtonType,
    ButtonVariant as DsButtonVariant,
    type ButtonColorType,
    type ButtonTypeType,
    type ButtonVariantType,
  } from '@tundr/ds-components';
  import { computed } from 'vue';
  import {
    modalActionsContainerClass,
    modalActionsContainerWithBorderClass,
  } from './ModalActions.css';
  import type { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';

  type ModalActionsProps = {
    confirmTestId?: string;
    cancelTestId?: string;
    confirmLabel?: string;
    disableConfirm?: boolean;
    cancelLabel?: string;
    disableCancel?: boolean;
    loadingConfirm?: boolean;
    loadingCancel?: boolean;
    withBorder?: boolean;
    cancelLabelVariant?: ButtonVariant;
    confirmLabelVariant?: ButtonVariant;
    cancelButtonIcon?: TUNDR_ICONS;
    confirmButtonIcon?: TUNDR_ICONS;
    confirmType?: ButtonTypeType;
  };

  const props = withDefaults(defineProps<ModalActionsProps>(), {
    withBorder: true,
    cancelLabelVariant: ButtonVariant.tertiary,
    confirmLabelVariant: ButtonVariant.primary,
  });

  type DsButtonAppearance = {
    variant: ButtonVariantType;
    color?: ButtonColorType;
  };

  const legacyToDs: Record<ButtonVariant, DsButtonAppearance> = {
    [ButtonVariant.primary]: { variant: DsButtonVariant.Primary },
    [ButtonVariant.secondary]: { variant: DsButtonVariant.Secondary },
    [ButtonVariant.tertiary]: { variant: DsButtonVariant.Text },
    [ButtonVariant.transparentOutlined]: { variant: DsButtonVariant.Secondary },
    [ButtonVariant.transparent]: { variant: DsButtonVariant.Text },
    [ButtonVariant.transparentUnderlined]: { variant: DsButtonVariant.Inline },
    [ButtonVariant.error]: {
      variant: DsButtonVariant.Primary,
      color: ButtonColor.Danger,
    },
    [ButtonVariant.success]: { variant: DsButtonVariant.Primary },
    [ButtonVariant.iconButton]: { variant: DsButtonVariant.Icon },
  };

  const toDsAppearance = (variant: ButtonVariant): DsButtonAppearance =>
    legacyToDs[variant];

  const cancelAppearance = computed(() =>
    toDsAppearance(props.cancelLabelVariant),
  );
  const confirmAppearance = computed(() =>
    toDsAppearance(props.confirmLabelVariant),
  );

  const modalActionsContainerClasses = computed(() => {
    return [
      modalActionsContainerClass,
      { [modalActionsContainerWithBorderClass]: props.withBorder },
    ];
  });

  defineEmits<{
    confirm: [];
    cancel: [];
  }>();
</script>

<template>
  <div :class="modalActionsContainerClasses">
    <Button
      :test-id="cancelTestId || 'cancel-button'"
      :variant="cancelAppearance.variant"
      :color="cancelAppearance.color"
      :disabled="disableCancel"
      v-if="cancelLabel"
      @click="$emit('cancel')"
      :loading="loadingCancel"
      :icon-name="cancelButtonIcon"
      >{{ cancelLabel }}
    </Button>
    <Button
      :test-id="confirmTestId || 'confirm-button'"
      :variant="confirmAppearance.variant"
      :color="confirmAppearance.color"
      :type="confirmType || ButtonType.Button"
      v-if="confirmLabel"
      :disabled="disableConfirm"
      :loading="loadingConfirm"
      @click="$emit('confirm')"
      :icon-name="confirmButtonIcon"
      >{{ confirmLabel }}
    </Button>
  </div>
</template>
